import { Outlet, useParams, useLocation, useNavigate } from "react-router";
import { Sidebar } from "./Sidebar";
import { BottomBar } from "./BottomBar";
import { Header } from "./Header";
import { useAuth } from "@/hooks/useAuth";
import { useHoliday } from "@/hooks/useHoliday";
import { useEffect } from "react";
import { Palmtree } from "lucide-react";
import { InstallBanner } from "@/components/shared/InstallBanner";

interface AppShellProps {
  role: "admin" | "client";
}

export function AppShell({ role }: AppShellProps) {
  const { jwt } = useParams();
  const { isAuthenticated, jwt: authJwt } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const holiday = useHoliday();

  // Redirect to passcode entry if not authenticated
  useEffect(() => {
    if (!isAuthenticated || authJwt !== jwt) {
      // Don't redirect if we're already on a passcode page
      const path = location.pathname;
      const isPasscodePage =
        path === `/admin/${jwt}` || path === `/client/${jwt}`;
      if (!isPasscodePage) {
        // Preserve current path so after login the user lands back here
        const redirectParam = encodeURIComponent(path);
        navigate(`/${role}/${jwt}?redirect=${redirectParam}`);
      }
    }
  }, [isAuthenticated, authJwt, jwt, role, navigate, location.pathname]);

  if (!isAuthenticated || authJwt !== jwt) {
    return null;
  }

  return (
    <div className="min-h-dvh bg-app-bg md:flex md:h-dvh md:overflow-hidden">
      {/* Sidebar: hidden on mobile, icon-only on tablet, full on desktop */}
      <Sidebar role={role} jwt={jwt!} />

      <div className="flex flex-col min-w-0 md:flex-1 md:min-h-0">
        <Header />
        <InstallBanner />
        {holiday.active && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm">
            <Palmtree className="w-4 h-4 shrink-0" />
            <span>
              🏖️ Holiday mode active
              {holiday.endsAt && (
                <> — ends {new Date(holiday.endsAt).toLocaleDateString()}</>
              )}
              {holiday.source === "admin" && " (set by admin)"}
            </span>
          </div>
        )}
        {/* Mobile: pt-14 for fixed header, pb-20 for fixed bottom bar; Desktop: overflow-auto in flex */}
        <main className="pt-14 pb-20 p-2 md:pt-0 md:pb-4 md:p-4 lg:p-6 md:flex-1 md:overflow-auto md:min-h-0">
          <div className="w-full max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom bar: mobile only */}
      <BottomBar role={role} jwt={jwt!} />
    </div>
  );
}
